import React, { Suspense, lazy, useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';


import NavBar from './components/navbar/NavBar';
import Footer from './components/footer/Footer';
import Loader from './components/common/Loader';

// Critical (above-the-fold) components can stay regular imports
import News from './components/news/News';
import Schedule from './components/schedule/Schedule';
import Contact from './components/contact/Contact';
import RegistrationForm from './components/registration/RegistrationForm';
import AboutUs from './components/aboutUs/AboutUs';
import Prices from './components/prices/Prices';
import RegisterButton from './components/registration/RegisterButton';
import QRCodePage from './components/qrCode/QRCode';
import Regulations from './components/regulations/Regulations';
import NewsDetails from './components/news/NewsDetails';
import { apiFetch } from './helpers/api';

// Lazy-loaded components (non-critical or admin)
const CalendarEvents = lazy(() => import('./components/calendar/CalendarEvents'));
const AdminEvents = lazy(() => import('./components/adminPanel/AdminEvents'));
const AdminDashboard = lazy(() => import('./components/adminPanel/AdminDashboard'));
const AdminLogin = lazy(() => import('./components/adminPanel/AdminLogin'));
const AdminNews = lazy(() => import('./components/adminPanel/AdminNews'));
const AdminContacts = lazy(() => import('./components/adminPanel/AdminContacts'));
const AdminSchedule = lazy(() => import('./components/adminPanel/AdminSchedule'));
const AdminLocations = lazy(() => import('./components/adminPanel/AdminLocations'));
const AdminCoaches = lazy(() => import('./components/adminPanel/AdminCoaches'));
const AdminSporters = lazy(() => import('./components/adminPanel/AdminSporters'));
const AdminGallery = lazy(() => import('./components/adminPanel/AdminGallery'));
const AdminPrices = lazy(() => import('./components/adminPanel/AdminPrices'));
const AdminProfiles = lazy(() => import('./components/adminPanel/AdminProfiles'));
const DojoGallery = lazy(() => import('./components/dojoGallery/DojoGallery'));
const AdminDojoGallery = lazy(() => import('./components/adminPanel/AdminDojoGallery'));

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // 🔒 Wrapper
  const ProtectedRoute = ({ isAuthenticated, children }) => {
    if (!isAuthenticated) {
      return <Navigate to="/admin" replace />;
    }
    return children;
  };

  // Check session on page load
  useEffect(() => {
    apiFetch("/api/auth/check", { credentials: "include" })
      .then((data) => { if (data?.loggedIn) setIsAuthenticated(true); })
      .catch(() => setIsAuthenticated(false));
  }, []);

  return (
    <div>
      <NavBar />

      <Suspense fallback={<div className="loader-center"><Loader /></div>}>
        <Routes>
          <Route path="/" element={<AboutUs />} />
          <Route path="/aboutUs" element={<AboutUs />} />
          <Route path="/news" element={<News />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/calendar" element={<CalendarEvents />} />
          <Route path="/register" element={<RegistrationForm />} />
          <Route path="/prices" element={<Prices />} />
          <Route path="/dojo" element={<DojoGallery />} />
          <Route path="/qrCode" element={<QRCodePage />} />
          <Route path="/regulations" element={<Regulations />} />
          <Route path="/news/:id" element={<NewsDetails />} />

          {/* Admin login (public) */}
          <Route
            path="/admin"
            element={
              isAuthenticated ? (
                <Navigate to="/admin/dashboard" replace />
              ) : (
                <AdminLogin setIsAuthenticated={setIsAuthenticated} />
              )
            }
          />

          {/* All protected admin routes under /admin/* */}
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute isAuthenticated={isAuthenticated}>
                <Routes>
                  <Route path="dashboard" element={<AdminDashboard setIsAuthenticated={setIsAuthenticated} />} />
                  <Route path="events" element={<AdminEvents />} />
                  <Route path="news" element={<AdminNews />} />
                  <Route path="schedule" element={<AdminSchedule />} />
                  <Route path="contacts" element={<AdminContacts />} />
                  <Route path="locations" element={<AdminLocations />} />
                  <Route path="coaches" element={<AdminCoaches />} />
                  <Route path="sporters" element={<AdminSporters />} />
                  <Route path="gallery" element={<AdminGallery />} />
                  <Route path="dojo-gallery" element={<AdminDojoGallery />} />
                  <Route path="prices" element={<AdminPrices />} />
                  <Route path="profiles" element={<AdminProfiles />} />
                </Routes>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Suspense>
      <RegisterButton />
      <Footer />
    </div>
  );
}

export default App;
